// Stable device-id + step-up client helpers for the Guided Scan flow.
// The device id is persistent per-browser (localStorage) and the backend
// binds every passkey / recovery-code authorization to (user, device).

import { api } from './api';

const DEVICE_ID_KEY = 'zita.device_id';
const DEVICE_LABEL_KEY = 'zita.device_label';

function _randDeviceId() {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function getDeviceId() {
  let v = localStorage.getItem(DEVICE_ID_KEY);
  if (!v) {
    v = _randDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, v);
  }
  return v;
}

export function getDeviceLabel() {
  return localStorage.getItem(DEVICE_LABEL_KEY) || guessDeviceLabel();
}

export function setDeviceLabel(label) {
  localStorage.setItem(DEVICE_LABEL_KEY, label);
}

export function guessDeviceLabel() {
  const ua = navigator.userAgent;
  if (/Macintosh|Mac OS X/.test(ua)) return 'This Mac';
  if (/Windows/.test(ua)) return 'This PC';
  if (/iPhone/.test(ua)) return 'This iPhone';
  if (/iPad/.test(ua)) return 'This iPad';
  if (/Android/.test(ua)) return 'This Android device';
  return 'This device';
}

// Base64url helpers
function b64uToBuf(b64u) {
  const pad = '='.repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}
function bufToB64u(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/=+$/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

export async function registerPasskey({ label } = {}) {
  if (!window.PublicKeyCredential) {
    throw new Error('This browser does not support passkeys.');
  }
  const device_id = getDeviceId();
  const device_label = getDeviceLabel();
  const opts = await api.post('/passkeys/registration/options', {
    device_id, device_label,
  });
  const json = JSON.parse(opts.data.publicKey);

  // Coerce challenge + user.id + excludeCredentials[].id back to ArrayBuffer
  json.challenge = b64uToBuf(json.challenge);
  json.user.id = b64uToBuf(json.user.id);
  if (Array.isArray(json.excludeCredentials)) {
    json.excludeCredentials = json.excludeCredentials.map((c) => ({
      ...c, id: b64uToBuf(c.id),
    }));
  }

  const cred = await navigator.credentials.create({ publicKey: json });
  const attestation = {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(cred.response.clientDataJSON),
      attestationObject: bufToB64u(cred.response.attestationObject),
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };

  const res = await api.post('/passkeys/registration/verify', {
    device_id,
    device_label,
    credential_label: label || 'Passkey',
    attestation_response: attestation,
  });
  return res.data;
}

export async function authenticateWithPasskey() {
  if (!window.PublicKeyCredential) {
    throw new Error('This browser does not support passkeys.');
  }
  const device_id = getDeviceId();
  const opts = await api.post('/passkeys/auth/options', { device_id });
  const json = JSON.parse(opts.data.publicKey);
  json.challenge = b64uToBuf(json.challenge);
  if (Array.isArray(json.allowCredentials)) {
    json.allowCredentials = json.allowCredentials.map((c) => ({
      ...c, id: b64uToBuf(c.id),
    }));
  }
  const cred = await navigator.credentials.get({ publicKey: json });
  const assertion = {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(cred.response.clientDataJSON),
      authenticatorData: bufToB64u(cred.response.authenticatorData),
      signature: bufToB64u(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };
  const res = await api.post('/passkeys/auth/verify', {
    device_id, assertion_response: assertion,
  });
  return res.data;
}

export async function redeemRecoveryCode(code) {
  const device_id = getDeviceId();
  const device_label = getDeviceLabel();
  const res = await api.post('/passkeys/recovery-codes/redeem', {
    device_id, device_label, code: code.trim(),
  });
  return res.data;
}

export async function generateRecoveryCodes() {
  const res = await api.post('/passkeys/recovery-codes/generate', {});
  return res.data;
}

export async function listPasskeys() {
  const { data } = await api.get('/passkeys');
  return data;
}
export async function renamePasskey(id, label) {
  return api.post(`/passkeys/${id}/rename`, { label });
}
export async function deletePasskey(id) {
  return api.delete(`/passkeys/${id}`);
}
export async function listTrustedDevices() {
  const { data } = await api.get('/trusted-devices');
  return data;
}
export async function renameTrustedDevice(id, label) {
  return api.post(`/trusted-devices/${id}/rename`, { label });
}
export async function revokeTrustedDevice(id) {
  return api.delete(`/trusted-devices/${id}`);
}
export async function revokeAllSessions() {
  return api.post('/sessions/revoke-all', {});
}

// Called by pages that trigger a Guided Scan action. Returns true if the
// authorization is live, or attempts step-up via passkey; caller is
// responsible for showing recovery-code fallback UI when passkey is refused.
export async function ensureGuidedScanAuthorization() {
  try {
    await authenticateWithPasskey();
    return true;
  } catch (e) {
    return false;
  }
}
